import {
  resolveYourImaiPersona,
  yourImaiBubbleColors,
  type YourImaiBubbleColors,
  type YourImaiPersona,
  type YourImaiPersonaInput,
} from './your-imai-persona';

/** The dispatch-source lookup for one message, as the hook reports it. */
export type YourImaiDispatchLookup = {
  status: 'pending' | 'success' | 'error';
  data: { persona: YourImaiPersonaInput } | null | undefined;
};

/**
 * How a dispatched message renders in a thread:
 * - `pending`: the lookup is in flight; hold the row at badge height.
 * - `default`: not the viewer's own IMAI (or the lookup failed); the normal
 *   bubble with its generic attribution.
 * - `badge`: the viewer's IMAI sent it; a compact "Message from {name}"
 *   badge in that IMAI's bubble colours, expanding to the whole prompt in
 *   place.
 */
export type YourImaiDispatchView =
  | { kind: 'pending' }
  | { kind: 'default' }
  | {
      kind: 'badge';
      persona: YourImaiPersona;
      label: string;
      colors: YourImaiBubbleColors;
      prompt: string;
    };

export function yourImaiDispatchView({
  lookup,
  prompt,
  defaultName,
  labelTemplate,
}: {
  lookup: YourImaiDispatchLookup;
  prompt: string;
  defaultName: string;
  /** Localized "Message from {name}". */
  labelTemplate: string;
}): YourImaiDispatchView {
  const text = prompt.trim();
  // Nothing to reveal on hover: keep whatever the bubble shows.
  if (!text || lookup.status === 'error') return { kind: 'default' };
  if (lookup.status === 'pending' || lookup.data === undefined)
    return { kind: 'pending' };
  if (!lookup.data) return { kind: 'default' };
  const persona = resolveYourImaiPersona(lookup.data.persona, defaultName);
  return {
    kind: 'badge',
    persona,
    label: labelTemplate.replace('{name}', persona.name),
    colors: yourImaiBubbleColors(persona.colorSeed),
    prompt: text,
  };
}
